import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '@/App';
import Layout from '@/components/Layout';
import TransactionModal from '@/components/TransactionModal';
import axios from 'axios';
import {
  CreditCard,
  Wallet,
  Building,
  Smartphone,
  TrendingUp,
  Plus,
  Edit2,
  Trash2,
  Archive,
  DollarSign,
  Eye,
  EyeOff,
  ArrowUpDown,
  MoreVertical,
  Search,
  Filter,
  X,
  HelpCircle,
  Info,
  ArrowRightLeft,
  Zap,
  Percent,
  Sparkles,
  Check,
  Lock
} from 'lucide-react';
import { showToast } from '@/lib/custom-toast';
import { formatCurrencyInput, parseCurrency } from '@/utils/currencyFormat';
import { getTodayLocalDateString } from '@/utils/dateUtils';
import CreditCardBreakdownModal from '@/components/CreditCardBreakdownModal';
import MobileBottomSheet from '@/components/MobileBottomSheet';

import BACKEND_URL from '@/config/backendUrl';


const ACCOUNT_TYPES = [
  { value: 'bank', label: 'Cuenta Bancaria / Débito', icon: Building },
  { value: 'cash', label: 'Efectivo', icon: Wallet },
  { value: 'credit_card', label: 'Tarjeta de Crédito', icon: CreditCard },
  { value: 'digital_wallet', label: 'Billetera Digital / Rendimiento', icon: Smartphone },
  { value: 'investment', label: 'Inversión', icon: TrendingUp }
];

const CURRENCIES = [
  { value: 'MXN', label: 'Peso Mexicano (MXN)', symbol: '$' },
  { value: 'USD', label: 'Dólar Americano (USD)', symbol: '$' }
];

const COLORS = [
  { value: '#3b82f6', label: 'Azul Eléctrico' },
  { value: '#10b981', label: 'Verde Esmeralda' },
  { value: '#06b6d4', label: 'Cyan' },
  { value: '#0d9488', label: 'Teal' },
  { value: '#6366f1', label: 'Índigo' },
  { value: '#8b5cf6', label: 'Morado' },
  { value: '#a855f7', label: 'Violeta' },
  { value: '#ec4899', label: 'Rosa Neón' },
  { value: '#f43f5e', label: 'Fucsia' },
  { value: '#ef4444', label: 'Rojo Coral' },
  { value: '#f97316', label: 'Naranja' },
  { value: '#f59e0b', label: 'Ámbar' },
  { value: '#eab308', label: 'Amarillo Dorado' },
  { value: '#84cc16', label: 'Lima' },
  { value: '#64748b', label: 'Pizarra' },
  { value: '#0f172a', label: 'Grafito Oscuro' }
];

const ACCOUNT_LIMITS = {
  free: 3,
  premium: 10,
  pro: Infinity
};

const Accounts = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, token } = useContext(AuthContext);
  const [accounts, setAccounts] = useState([]);
  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [highlightReminder, setHighlightReminder] = useState(false);
  const [hideBalances, setHideBalances] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Transaction Modal states
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  const [transactionSourceAccountId, setTransactionSourceAccountId] = useState(null);
  const [isBreakdownModalOpen, setIsBreakdownModalOpen] = useState(false);
  const [selectedAccountForBreakdown, setSelectedAccountForBreakdown] = useState(null);

  // Drag down to close account modal
  const [accountDragStartY, setAccountDragStartY] = useState(0);
  const [accountDragCurrentY, setAccountDragCurrentY] = useState(0);
  const [isAccountDragging, setIsAccountDragging] = useState(false);

  // Search, Filter and Tab states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCurrency, setFilterCurrency] = useState('all');
  const [sortBy, setSortBy] = useState('name'); // name, balance-high, balance-low, date-new, date-old
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'debit' | 'credit' | 'yield'
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [showSortModal, setShowSortModal] = useState(false);
  const [accountForActions, setAccountForActions] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    type: 'bank',
    currency: 'MXN',
    initial_balance: 0,
    credit_limit: null,
    credit_card_type: 'bank',
    cut_day: '',
    payment_due_day: '',
    payment_due_offset_months: 0,
    auto_reminder: false,
    color: '#3b82f6',
    icon: 'bank',
    is_default: false,
    has_yield: false,
    annual_yield_rate: '',
    yield_start_date: getTodayLocalDateString(),
    bank_name: '',
    clabe: '',
    holder_name: ''
  });

  useEffect(() => {
    if (token) {
      fetchAccounts();
      fetchDebts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchDebts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/debts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDebts(response.data || []);
    } catch (e) {
      console.warn('Error fetching debts:', e);
    }
  };

  // Listener para refrescar balances de cuentas automáticamente en tiempo real
  useEffect(() => {
    const handleTransactionAdded = () => {
      if (token) fetchAccounts();
    };
    window.addEventListener('transaction-added', handleTransactionAdded);
    return () => window.removeEventListener('transaction-added', handleTransactionAdded);
  }, [token]);

  // Lock body scroll when dialog is open
  useEffect(() => {
    if (isDialogOpen || showDeleteConfirm) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isDialogOpen, showDeleteConfirm]);

  // Clear credit_limit when type is not credit_card
  useEffect(() => {
    if (formData.type !== 'credit_card') {
      setFormData(prev => ({ ...prev, credit_limit: null }));
    }
  }, [formData.type]);

  // Scroll suave y foco hacia la cuenta visitada previamente al volver del detalle
  useEffect(() => {
    const returnAccountId = sessionStorage.getItem('return_to_account_id');
    if (!returnAccountId || loading || accounts.length === 0) return;

    let attempts = 0;
    let timerId = null;

    const tryScrollToCard = () => {
      attempts++;
      const el = document.getElementById(`account-card-${returnAccountId}`);
      if (el) {
        sessionStorage.removeItem('return_to_account_id');
        // Calcular posición precisa con margen superior para el header móvil/desktop
        const yOffset = -90;
        const rect = el.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
        const targetY = rect.top + scrollTop + yOffset;

        window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Resaltar tarjeta visualmente
        el.classList.add('ring-4', 'ring-indigo-500/60', 'ring-offset-2', 'dark:ring-offset-slate-900', 'transition-all');
        setTimeout(() => {
          el.classList.remove('ring-4', 'ring-indigo-500/60', 'ring-offset-2', 'dark:ring-offset-slate-900');
        }, 2200);
      } else if (attempts < 25) {
        timerId = setTimeout(tryScrollToCard, 60);
      } else {
        sessionStorage.removeItem('return_to_account_id');
      }
    };

    // Iniciar intento de scroll
    timerId = setTimeout(tryScrollToCard, 50);

    return () => {
      if (timerId) clearTimeout(timerId);
    };
  }, [loading, accounts]);

  // Auto-abrir modal de edición y resaltar sección de recordatorio si viene de PaymentReminders (?editAccountId=...&highlight=reminder)
  useEffect(() => {
    const editAccountId = searchParams.get('editAccountId');
    const highlight = searchParams.get('highlight');
    if (!editAccountId || loading || accounts.length === 0) return;

    const target = accounts.find(acc => String(acc.id || acc._id) === String(editAccountId));
    if (target) {
      handleOpenDialog(target);
      if (highlight === 'reminder') {
        setHighlightReminder(true);
        // Scroll y foco dentro del modal hacia la sección de fechas/recordatorio
        setTimeout(() => {
          const reminderSection = document.getElementById('account-reminder-section');
          if (reminderSection) {
            reminderSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 250);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, loading, accounts]);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const sortedData = [...response.data].sort((a, b) => {
        if (a.is_default && !b.is_default) return -1;
        if (!a.is_default && b.is_default) return 1;
        return (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' });
      });
      setAccounts(sortedData);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      showToast.error('Error al cargar cuentas');
    } finally {
      setLoading(false);
    }
  };

  const canCreateAccount = () => {
    const tier = user?.subscription_tier || 'free';
    const limit = ACCOUNT_LIMITS[tier];
    return accounts.length < limit;
  };

  const getAccountLimit = () => {
    const tier = user?.subscription_tier || 'free';
    const limit = ACCOUNT_LIMITS[tier];
    return limit === Infinity ? 'Ilimitadas' : limit;
  };

  const handleOpenDialog = (account = null) => {
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    if (account) {
      setEditingAccount(account);
      setFormData({
        name: account.name,
        type: account.type,
        currency: account.currency,
        initial_balance: account.current_balance,
        credit_limit: account.credit_limit,
        credit_card_type: account.credit_card_type || 'bank',
        cut_day: account.cut_day || '',
        payment_due_day: account.payment_due_day || '',
        payment_due_offset_months: account.payment_due_offset_months !== undefined && account.payment_due_offset_months !== null ? Number(account.payment_due_offset_months) : 0,
        auto_reminder: account.auto_reminder || false,
        color: account.color,
        icon: account.icon,
        is_default: account.is_default || false,
        has_yield: account.has_yield || false,
        annual_yield_rate: account.annual_yield_rate ? String(account.annual_yield_rate) : '',
        yield_start_date: account.yield_start_date ? account.yield_start_date.split('T')[0] : getTodayLocalDateString(),
        bank_name: account.bank_name || '',
        clabe: account.clabe || '',
        holder_name: account.holder_name || ''
      });
    } else {
      setEditingAccount(null);
      setFormData({
        name: '',
        type: 'bank',
        currency: 'MXN',
        initial_balance: 0,
        credit_limit: null,
        credit_card_type: 'bank',
        cut_day: '',
        payment_due_day: '',
        payment_due_offset_months: 0,
        auto_reminder: false,
        color: '#3b82f6',
        icon: 'bank',
        is_default: false,
        has_yield: false,
        annual_yield_rate: '',
        yield_start_date: getTodayLocalDateString(),
        bank_name: '',
        clabe: '',
        holder_name: ''
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingAccount(null);
    setHighlightReminder(false);
    if (searchParams.get('editAccountId') || searchParams.get('highlight')) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('editAccountId');
      nextParams.delete('highlight');
      setSearchParams(nextParams, { replace: true });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canCreateAccount() && !editingAccount) {
      const tier = user?.subscription_tier || 'free';
      const nextTier = tier === 'free' ? 'Premium' : 'Pro';
      showToast.error(`Límite alcanzado. Actualiza a ${nextTier} para más cuentas.`);
      return;
    }

    const isYieldType = ['digital_wallet', 'investment'].includes(formData.type);

    try {
      const endpoint = editingAccount
        ? `${BACKEND_URL}/api/accounts/${editingAccount.id}`
        : `${BACKEND_URL}/api/accounts`;

      const method = editingAccount ? 'PUT' : 'POST';

      const payload = editingAccount ? {
        name: formData.name,
        type: formData.type,
        currency: formData.currency,
        credit_limit: formData.type === 'credit_card' ? parseFloat(formData.credit_limit) : null,
        credit_card_type: formData.type === 'credit_card' ? (formData.credit_card_type || 'bank') : null,
        cut_day: formData.type === 'credit_card' && formData.cut_day ? parseInt(formData.cut_day) : null,
        payment_due_day: formData.type === 'credit_card' && formData.payment_due_day ? parseInt(formData.payment_due_day) : null,
        payment_due_offset_months: formData.type === 'credit_card' ? (parseInt(formData.payment_due_offset_months, 10) || 0) : 0,
        auto_reminder: formData.type === 'credit_card' ? formData.auto_reminder : false,
        color: formData.color,
        icon: formData.icon,
        is_default: formData.is_default,
        has_yield: isYieldType ? formData.has_yield : false,
        annual_yield_rate: isYieldType && formData.has_yield ? parseFloat(formData.annual_yield_rate) || 0 : 0,
        yield_start_date: isYieldType && formData.has_yield ? formData.yield_start_date : null,
        bank_name: formData.bank_name?.trim() || null,
        clabe: formData.clabe?.replace(/\D/g, '') || null,
        holder_name: formData.holder_name?.trim() || null
      } : {
        ...formData,
        initial_balance: parseFloat(formData.initial_balance),
        credit_limit: formData.type === 'credit_card' ? parseFloat(formData.credit_limit) : null,
        credit_card_type: formData.type === 'credit_card' ? (formData.credit_card_type || 'bank') : null,
        cut_day: formData.type === 'credit_card' && formData.cut_day ? parseInt(formData.cut_day) : null,
        payment_due_day: formData.type === 'credit_card' && formData.payment_due_day ? parseInt(formData.payment_due_day) : null,
        payment_due_offset_months: formData.type === 'credit_card' ? (parseInt(formData.payment_due_offset_months, 10) || 0) : 0,
        auto_reminder: formData.type === 'credit_card' ? formData.auto_reminder : false,
        has_yield: isYieldType ? formData.has_yield : false,
        annual_yield_rate: isYieldType && formData.has_yield ? parseFloat(formData.annual_yield_rate) || 0 : 0,
        yield_start_date: isYieldType && formData.has_yield ? formData.yield_start_date : null,
        bank_name: formData.bank_name?.trim() || null,
        clabe: formData.clabe?.replace(/\D/g, '') || null,
        holder_name: formData.holder_name?.trim() || null
      };

      const response = editingAccount
        ? await axios.put(endpoint, payload, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })
        : await axios.post(endpoint, payload, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

      const savedAccount = response.data;

      // Crear recordatorio automático si se activó y es tarjeta de crédito
      if (!editingAccount && formData.type === 'credit_card' && formData.auto_reminder && formData.payment_due_day) {
        try {
          await axios.post(`${BACKEND_URL}/api/payment-reminders`, {
            title: `Pago ${formData.name}`,
            description: `Este recordatorio fue creado desde Cuentas. Para cambiar monto o fecha de corte, edita la cuenta.`,
            category: 'credit_card',
            frequency: 'monthly',
            due_date: parseInt(formData.payment_due_day),
            notification_days: 3,
            notification_time: '09:00',
            all_day: false,
            source: 'account',
            linked_account_id: savedAccount.id || savedAccount._id
          }, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          showToast.success('Cuenta creada y recordatorio de pago generado');
        } catch (reminderErr) {
          console.error('Error al crear recordatorio:', reminderErr);
          showToast.success('Cuenta creada exitosamente');
          showToast.error('No se pudo crear el recordatorio automático');
        }
      } else {
        showToast.success(editingAccount ? 'Cuenta actualizada' : 'Cuenta creada exitosamente');
      }

      handleCloseDialog();
      fetchAccounts();
    } catch (error) {
      console.error('Error saving account:', error);
      const msg = error.response?.data?.detail || error.message || 'Error al guardar cuenta';
      showToast.error(msg);
    }
  };

  const handleDeleteAccount = async (accountId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/accounts/${accountId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      showToast.success('Cuenta eliminada exitosamente');
      setShowDeleteConfirm(false);
      setSelectedAccount(null);
      fetchAccounts();
    } catch (error) {
      console.error('Error deleting account:', error);
      showToast.error(error.response?.data?.detail || error.message || 'Error al eliminar cuenta');
      setShowDeleteConfirm(false);
      setSelectedAccount(null);
    }
  };

  const handleArchiveAccount = async (accountId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/accounts/${accountId}`, { is_active: false }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      showToast.success('Cuenta archivada exitosamente');
      fetchAccounts();
    } catch (error) {
      console.error('Error archiving account:', error);
      showToast.error(error.response?.data?.detail || error.message || 'Error al archivar cuenta');
    }
  };

  const formatCurrency = (amount, currency) => {
    const symbol = currency === 'USD' ? '$' : '$';
    return `${symbol}${amount.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getTypeIcon = (type) => {
    const accountType = ACCOUNT_TYPES.find(t => t.value === type);
    return accountType ? accountType.icon : Building;
  };

  const getTotalAssets = () => {
    return accounts.reduce((sum, account) => {
      if (account.type === 'credit_card' || !account.is_active) return sum;
      const amt = account.currency === 'USD' ? account.current_balance * 17 : account.current_balance;
      return sum + amt;
    }, 0);
  };

  const getTotalCreditDebt = () => {
    return accounts.reduce((sum, account) => {
      if (account.type !== 'credit_card' || !account.is_active) return sum;
      const debt = Math.abs(account.current_balance || 0);
      const amt = account.currency === 'USD' ? debt * 17 : debt;
      return sum + amt;
    }, 0);
  };

  const getTotalBalance = () => {
    return getTotalAssets() - getTotalCreditDebt();
  };

  // Deudas personales: lo que debo yo vs lo que me deben
  const getDebtBreakdown = () => {
    const activeDebts = (debts || []).filter(d => d.status === 'active');
    const iOwe = activeDebts.filter(d => d.debt_direction === 'i_owe');
    const theyOweMe = activeDebts.filter(d => d.debt_direction === 'they_owe_me');
    const totalIOwe = iOwe.reduce((s, d) => s + (d.current_balance || 0), 0);
    const totalOwedToMe = theyOweMe.reduce((s, d) => s + (d.current_balance || 0), 0);
    return { totalIOwe, totalOwedToMe, iOweCount: iOwe.length, theyOweMeCount: theyOweMe.length };
  };

  // Filter and sort accounts
  const getFilteredAndSortedAccounts = () => {
    let filtered = [...accounts];

    // Tab filter
    if (activeTab === 'debit') {
      filtered = filtered.filter(a => a.type !== 'credit_card');
    } else if (activeTab === 'credit') {
      filtered = filtered.filter(a => a.type === 'credit_card');
    } else if (activeTab === 'yield') {
      filtered = filtered.filter(a => a.has_yield);
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(account =>
        account.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Currency filter
    if (filterCurrency !== 'all') {
      filtered = filtered.filter(account => account.currency === filterCurrency);
    }

    // Sort: Siempre cuenta predeterminada hasta arriba, y lo demás por orden alfabético
    filtered.sort((a, b) => {
      if (a.is_default && !b.is_default) return -1;
      if (!a.is_default && b.is_default) return 1;

      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' });
        case 'balance-high':
          return b.current_balance - a.current_balance;
        case 'balance-low':
          return a.current_balance - b.current_balance;
        case 'date-new':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case 'date-old':
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        default:
          return (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' });
      }
    });

    return filtered;
  };

  const filteredAccounts = getFilteredAndSortedAccounts();

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-2">
                  Mis Cuentas
                </h1>
                <p className="text-sm md:text-base text-slate-600 dark:text-slate-400">
                  Gestiona tus cuentas bancarias, tarjetas y efectivo
                </p>
              </div>
              <div className="hidden md:flex items-center gap-3">
                <button
                  onClick={() => setHideBalances(!hideBalances)}
                  className="p-2 rounded-lg bg-white dark:bg-slate-800 shadow hover:shadow-md transition-shadow"
                >
                  {hideBalances ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => handleOpenDialog()}
                  disabled={!canCreateAccount()}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed"
                >
                  {!canCreateAccount() ? <Lock className="w-4 h-4" /> : <Plus className="w-5 h-5" />}
                  <span>Nueva Cuenta</span>
                </button>
              </div>
            </div>

            {/* Mobile button - Full width */}
            <div className="flex md:hidden items-center gap-3">
              <button
                onClick={() => setHideBalances(!hideBalances)}
                className="p-3 rounded-lg bg-white dark:bg-slate-800 shadow hover:shadow-md transition-shadow"
              >
                {hideBalances ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
              <button
                onClick={() => handleOpenDialog()}
                disabled={!canCreateAccount()}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-slate-400 disabled:cursor-not-allowed font-medium"
              >
                {!canCreateAccount() ? <Lock className="w-4 h-4" /> : <Plus className="w-5 h-5" />}
                <span>Agregar Cuenta</span>
              </button>
            </div>
          </div>

          {/* Account Limit Info */}
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-900 dark:text-blue-100 flex items-center flex-wrap gap-1">
              <span className="font-semibold">{accounts.length} de {getAccountLimit()}</span> cuentas utilizadas
              {!canCreateAccount() && (
                <span className="inline-flex items-center gap-1 text-amber-700 font-bold ml-1">
                  • <Lock className="w-3.5 h-3.5 inline" /> Límite alcanzado ({getAccountLimit()}).
                  <button onClick={() => navigate('/pricing')} className="underline hover:text-blue-700 ml-1">
                    Actualiza tu plan
                  </button>
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Summary Cards: 4 cards compactas */}
        {(() => {
          const { totalIOwe, totalOwedToMe, iOweCount, theyOweMeCount } = getDebtBreakdown();
          const totalAssets = getTotalAssets();
          const totalCreditDebt = getTotalCreditDebt();
          const patrimonioBase = totalAssets - totalCreditDebt;
          const patrimonioAjustado = patrimonioBase + totalOwedToMe;
          const isPositive = patrimonioAjustado >= 0;
          return (
            <>
              {/* 4 cards: 2 cols en móvil, 4 cols en desktop */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">

                {/* Total Activos */}
                <div className="relative overflow-hidden rounded-2xl p-4 bg-gradient-to-br from-emerald-600 to-teal-700 text-white shadow-md">
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-[10px] font-black uppercase tracking-widest opacity-80">Total Activos</span>
                    <Wallet className="w-4 h-4 opacity-70 shrink-0" />
                  </div>
                  <p className="text-xl sm:text-2xl font-black leading-tight">
                    {hideBalances ? '••••••' : formatCurrency(totalAssets, 'MXN')}
                  </p>
                  <p className="text-[10px] opacity-70 mt-1">Bancos, efectivo y rendimientos</p>
                </div>

                {/* Deuda Tarjetas */}
                <div className="relative overflow-hidden rounded-2xl p-4 bg-gradient-to-br from-rose-600 to-pink-700 text-white shadow-md">
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-[10px] font-black uppercase tracking-widest opacity-80">Deuda Tarjetas</span>
                    <CreditCard className="w-4 h-4 opacity-70 shrink-0" />
                  </div>
                  <p className="text-xl sm:text-2xl font-black leading-tight">
                    {hideBalances ? '••••••' : `-${formatCurrency(totalCreditDebt, 'MXN')}`}
                  </p>
                  <p className="text-[10px] opacity-70 mt-1">Por pagar a bancos</p>
                </div>

                {/* Me Deben */}
                <div className="relative overflow-hidden rounded-2xl p-4 bg-gradient-to-br from-sky-600 to-indigo-700 text-white shadow-md">
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-[10px] font-black uppercase tracking-widest opacity-80">Me Deben</span>
                    <ArrowRightLeft className="w-4 h-4 opacity-70 shrink-0" />
                  </div>
                  <p className="text-xl sm:text-2xl font-black leading-tight">
                    {hideBalances ? '••••••' : `+${formatCurrency(totalOwedToMe, 'MXN')}`}
                  </p>
                  <p className="text-[10px] opacity-70 mt-1">{theyOweMeCount} deuda{theyOweMeCount !== 1 ? 's' : ''} activa{theyOweMeCount !== 1 ? 's' : ''}</p>
                </div>

                {/* Patrimonio Ajustado */}
                <div className={`relative overflow-hidden rounded-2xl p-4 text-white shadow-md bg-gradient-to-br ${
                  isPositive ? 'from-indigo-700 via-purple-700 to-slate-900' : 'from-slate-800 via-rose-900 to-slate-900'
                }`}>
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-[10px] font-black uppercase tracking-widest opacity-80">Patrimonio Real</span>
                    <TrendingUp className="w-4 h-4 opacity-70 shrink-0" />
                  </div>
                  <p className={`text-xl sm:text-2xl font-black leading-tight ${isPositive ? '' : 'text-rose-300'}`}>
                    {hideBalances ? '••••••' : formatCurrency(patrimonioAjustado, 'MXN')}
                  </p>
                  <p className="text-[10px] opacity-70 mt-1">Activos − Tarjetas + Me deben</p>
                </div>
              </div>
            </>
          );
        })()}

        {/* Píldoras de Filtro Rápido */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-6 scrollbar-none">
          {[
            { id: 'all', label: 'Todas las cuentas', count: accounts.length },
            { id: 'debit', label: 'Débito y Efectivo', count: accounts.filter(a => a.type !== 'credit_card').length },
            { id: 'credit', label: 'Tarjetas de Crédito', count: accounts.filter(a => a.type === 'credit_card').length },
            { id: 'yield', label: '⚡ Con Rendimiento', count: accounts.filter(a => a.has_yield).length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-1.5 ${
                activeTab === tab.id
                  ? 'bg-slate-900 text-white shadow-sm'
                  : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50'
              }`}
            >
              <span>{tab.label}</span>
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Search and Filters — estilo Movimientos */}
        {accounts.length > 0 && (
          <div className="mb-4 space-y-2">
            {/* Barra de búsqueda y herramientas (Filtro Moneda + Organizar) */}
            <div className="flex items-center gap-2 pt-0.5">
              <div className="flex-1 relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar cuenta..."
                  className="w-full h-12 pl-10 pr-8 bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all shadow-xs font-medium dark:text-white"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Botón Filtro Moneda */}
              <button
                type="button"
                onClick={() => setShowCurrencyModal(true)}
                className={`relative h-12 w-12 min-w-[48px] rounded-2xl border transition-all flex items-center justify-center shrink-0 active:scale-95 shadow-xs ${
                  filterCurrency !== 'all'
                    ? 'bg-blue-50 border-blue-300 text-blue-600 font-bold dark:bg-blue-950/50 dark:border-blue-700 dark:text-blue-300'
                    : 'bg-white dark:bg-slate-800 border-slate-200/80 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-750'
                }`}
                title="Filtrar por moneda"
                aria-label="Filtrar por moneda"
              >
                <Filter className="w-4 h-4" />
                {filterCurrency !== 'all' && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-600 text-white rounded-full text-[10px] font-black flex items-center justify-center shadow-xs animate-in zoom-in-50">
                    1
                  </span>
                )}
              </button>

              {/* Botón Organizar */}
              <button
                type="button"
                onClick={() => setShowSortModal(true)}
                className={`relative h-12 w-12 min-w-[48px] rounded-2xl border transition-all flex items-center justify-center shrink-0 active:scale-95 shadow-xs ${
                  sortBy !== 'name'
                    ? 'bg-blue-50 border-blue-300 text-blue-600 font-bold dark:bg-blue-950/50 dark:border-blue-700 dark:text-blue-300'
                    : 'bg-white dark:bg-slate-800 border-slate-200/80 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-750'
                }`}
                title="Organizar cuentas"
                aria-label="Organizar cuentas"
              >
                <ArrowUpDown className="w-4 h-4" />
                {sortBy !== 'name' && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-blue-600 text-white rounded-full text-[10px] font-black flex items-center justify-center shadow-xs animate-in zoom-in-50">
                    1
                  </span>
                )}
              </button>
            </div>

            {/* Píldoras de Filtros Activos (estilo Movimientos) */}
            {(filterCurrency !== 'all' || sortBy !== 'name') && (
              <div className="flex items-center gap-1.5 flex-wrap px-1 pt-0.5">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Activos:</span>
                {filterCurrency !== 'all' && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-xs font-bold bg-blue-50 text-blue-700 border border-blue-100 shadow-xs dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800">
                    <span>Moneda: {filterCurrency}</span>
                    <button
                      type="button"
                      onClick={() => setFilterCurrency('all')}
                      className="p-0.5 hover:bg-blue-200 rounded-full dark:hover:bg-blue-800 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {sortBy !== 'name' && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-xs font-bold bg-blue-50 text-blue-700 border border-blue-100 shadow-xs dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800">
                    <span>
                      Orden: {
                        sortBy === 'balance-high' ? '$ Mayor a Menor' :
                        sortBy === 'balance-low' ? '$ Menor a Mayor' :
                        sortBy === 'date-new' ? 'Más Reciente' : 'Más Antigua'
                      }
                    </span>
                    <button
                      type="button"
                      onClick={() => setSortBy('name')}
                      className="p-0.5 hover:bg-blue-200 rounded-full dark:hover:bg-blue-800 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
              </div>
            )}

            {/* Contador de resultados */}
            {filteredAccounts.length !== accounts.length && (
              <div className="text-xs text-slate-500 dark:text-slate-400 px-1 font-medium">
                Mostrando {filteredAccounts.length} de {accounts.length} cuentas
              </div>
            )}
          </div>
        )}

        {/* Accounts Grid */}
        {accounts.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl shadow">
            <Wallet className="w-16 h-16 mx-auto text-slate-400 mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
              No tienes cuentas aún
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              Crea tu primera cuenta para empezar a gestionar tus finanzas
            </p>
            <button
              onClick={() => handleOpenDialog()}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Crear Primera Cuenta
            </button>
          </div>
        ) : filteredAccounts.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl shadow">
            <Search className="w-16 h-16 mx-auto text-slate-400 mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
              No se encontraron cuentas
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              Intenta ajustar los filtros de búsqueda
            </p>
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterCurrency('all');
                setSortBy('name');
              }}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
            >
              <X className="w-5 h-5" />
              Limpiar Filtros
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredAccounts.map((account) => {
              const IconComponent = getTypeIcon(account.type);
              const isCreditCard = account.type === 'credit_card';
              const availableCredit = account.credit_limit ? Math.max(0, account.credit_limit - Math.abs(account.current_balance || 0)) : null;

              return (
                <div
                  key={account.id}
                  id={`account-card-${account.id}`}
                  onClick={() => {
                    sessionStorage.setItem('return_to_account_id', account.id);
                    navigate(`/accounts/${account.id}`);
                  }}
                  className="group bg-white dark:bg-slate-800 rounded-2xl border border-slate-200/80 dark:border-slate-700/70 p-3.5 hover:shadow-md hover:border-slate-300 dark:hover:border-slate-600 transition-all cursor-pointer active:scale-[0.99] relative overflow-hidden"
                  style={{ borderLeft: `4px solid ${account.color}` }}
                >
                  {/* Fila Principal */}
                  <div className="flex items-center gap-3">
                    {/* Icono con fondo suave del color de la cuenta */}
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 shadow-xs"
                      style={{ backgroundColor: `${account.color}18` }}
                    >
                      <IconComponent className="w-5 h-5" style={{ color: account.color }} />
                    </div>

                    {/* Información Central */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <h3 className="font-bold text-sm text-slate-900 dark:text-white truncate">
                          {account.name}
                        </h3>
                        {account.is_default && (
                          <span className="shrink-0 px-1.5 py-0.2 bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 rounded text-[10px] font-bold">
                            Default
                          </span>
                        )}
                      </div>

                      {/* Subtítulo: Tipo + Moneda + Rendimiento */}
                      <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                        <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                          {ACCOUNT_TYPES.find(t => t.value === account.type)?.label || account.type}
                        </span>
                        <span className="px-1.5 py-0.2 bg-slate-100 dark:bg-slate-700 text-[10px] font-bold text-slate-600 dark:text-slate-300 rounded">
                          {account.currency}
                        </span>
                        {account.has_yield && account.annual_yield_rate > 0 && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.2 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 rounded text-[10px] font-bold">
                            <Zap className="w-2.5 h-2.5 text-amber-500" />
                            {account.annual_yield_rate}%
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Saldo a la derecha + Menú */}
                    <div className="text-right shrink-0 flex items-center gap-1.5">
                      <div>
                        <p className={`text-[10px] font-black uppercase tracking-wider leading-tight ${
                          isCreditCard ? 'text-rose-500 dark:text-rose-400' : 'text-slate-400 dark:text-slate-500'
                        }`}>
                          {isCreditCard ? 'Deuda' : 'Saldo'}
                        </p>
                        <p className={`text-base font-black leading-tight ${
                          isCreditCard ? 'text-rose-600 dark:text-rose-400' : 'text-slate-900 dark:text-white'
                        }`}>
                          {hideBalances ? '••••••' : isCreditCard
                            ? `-${formatCurrency(Math.abs(account.current_balance || 0), account.currency)}`
                            : formatCurrency(account.current_balance, account.currency)}
                        </p>
                      </div>

                      {/* Botón de Opciones (abre ActionSheet deslizable) */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setAccountForActions(account);
                        }}
                        className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                        title="Opciones de cuenta"
                        aria-label="Opciones de cuenta"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Sub-fila compacta para Tarjeta de Crédito */}
                  {isCreditCard && (
                    <div className="mt-2.5 pt-2 border-t border-slate-100 dark:border-slate-700/60 flex items-center justify-between gap-2 text-xs">
                      <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-[11px] truncate">
                        {account.credit_limit && (
                          <span>Lím: {formatCurrency(account.credit_limit, account.currency)}</span>
                        )}
                        {availableCredit !== null && (
                          <span className="font-bold text-emerald-600 dark:text-emerald-400">
                            · Disp: {hideBalances ? '••••' : formatCurrency(availableCredit, account.currency)}
                          </span>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.currentTarget?.blur();
                          setSelectedAccountForBreakdown(account);
                          setIsBreakdownModalOpen(true);
                        }}
                        className="py-1 px-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-lg text-[11px] font-bold shadow-xs flex items-center gap-1 active:scale-95 transition-all shrink-0"
                      >
                        <CreditCard className="w-3 h-3" />
                        <span>Desglosar</span>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Botón agregar cuenta al final de la lista */}
        {canCreateAccount() && (
          <div className="mt-4">
            <button
              type="button"
              onClick={() => handleOpenDialog()}
              className="w-full py-3 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-2xl text-slate-500 dark:text-slate-400 hover:border-blue-400 hover:text-blue-500 dark:hover:border-blue-500 dark:hover:text-blue-400 transition-all flex items-center justify-center gap-2 text-sm font-semibold group"
            >
              <Plus className="w-4 h-4 group-hover:scale-110 transition-transform" />
              Agregar otra cuenta
            </button>
          </div>
        )}

        {/* Create/Edit Account Dialog */}
        {isDialogOpen && (
          <div
            className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 backdrop-blur-sm"
            onClick={(e) => { if (e.target === e.currentTarget) handleCloseDialog(); }}
          >
            {/* Bottom sheet en móvil, modal centrado en desktop */}
            <div
              style={{
                transform: isAccountDragging && accountDragCurrentY > 0 ? `translateY(${accountDragCurrentY}px)` : undefined,
                transition: isAccountDragging ? 'none' : 'transform 0.2s ease-out'
              }}
              className="bg-white dark:bg-slate-800 w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl shadow-2xl max-h-[93vh] sm:max-h-[90vh] overflow-y-auto"
            >
              {/* Handle bar para arrastrar hacia abajo visible en móvil */}
              <div
                onTouchStart={(e) => {
                  setAccountDragStartY(e.touches[0].clientY);
                  setIsAccountDragging(true);
                }}
                onTouchMove={(e) => {
                  if (!isAccountDragging) return;
                  const diff = e.touches[0].clientY - accountDragStartY;
                  if (diff > 0) setAccountDragCurrentY(diff);
                }}
                onTouchEnd={() => {
                  setIsAccountDragging(false);
                  if (accountDragCurrentY > 75) {
                    handleCloseDialog();
                  }
                  setAccountDragCurrentY(0);
                }}
                className="flex justify-center pt-3 pb-2 sm:hidden cursor-grab active:cursor-grabbing select-none"
              >
                <div className="w-12 h-1.5 bg-slate-300 dark:bg-slate-600 rounded-full" />
              </div>
              <div className="p-6">
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">
                  {editingAccount ? 'Editar Cuenta' : 'Nueva Cuenta'}
                </h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Nombre de la Cuenta *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ej: BBVA Débito, Efectivo, etc."
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
                    />
                  </div>

                  {/* Type */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Tipo de Cuenta *
                    </label>
                    <select
                      required
                      value={formData.type}
                      onChange={(e) => {
                        const newType = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          type: newType,
                          // Reset credit_limit si no es credit_card
                          credit_limit: newType === 'credit_card' ? prev.credit_limit : null,
                          // Reset cut/payment days si no es credit_card
                          cut_day: newType === 'credit_card' ? prev.cut_day : '',
                          payment_due_day: newType === 'credit_card' ? prev.payment_due_day : '',
                          auto_reminder: newType === 'credit_card' ? prev.auto_reminder : false,
                          // Reset yield si no es digital_wallet ni investment
                          has_yield: ['digital_wallet', 'investment'].includes(newType) ? prev.has_yield : false,
                          annual_yield_rate: ['digital_wallet', 'investment'].includes(newType) ? prev.annual_yield_rate : '',
                        }));
                      }}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
                    >
                      {ACCOUNT_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Currency */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Moneda *
                    </label>
                    <select
                      required
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
                    >
                      {CURRENCIES.map(curr => (
                        <option key={curr.value} value={curr.value}>{curr.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Initial Balance / Saldo Actual (for credit cards) */}
                  {!editingAccount && (
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        {formData.type === 'credit_card' ? 'Deuda Actual' : 'Saldo Inicial'} *
                        {formData.type === 'credit_card' && (
                          <div className="relative group">
                            <HelpCircle className="w-4 h-4 text-blue-500 cursor-help" />
                            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 sm:w-72 p-3 bg-slate-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-[100] shadow-xl pointer-events-none">
                              <p className="font-semibold mb-1">💳 Deuda Actual</p>
                              <p>Ingresa cuánto le debes actualmente al banco.</p>
                              <p className="mt-2 text-yellow-300">Ejemplo: Si debes $5,000 → ingresa 5000</p>
                              {/* Arrow pointing down */}
                              <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-slate-900"></div>
                            </div>
                          </div>
                        )}
                      </label>
                      {formData.type === 'credit_card' && (
                        <p className="text-xs text-blue-600 dark:text-blue-400 mb-2">
                          💡 Ingresa la cantidad que le debes al banco
                        </p>
                      )}
                      <input
                        type="text"
                        inputMode="decimal"
                        required
                        value={formData.type === 'credit_card'
                          ? (formData.initial_balance !== 0 && formData.initial_balance !== '' && formData.initial_balance !== '-' ? formatCurrencyInput(String(formData.initial_balance).replace('-', '')) : '')
                          : (formData.initial_balance ? formatCurrencyInput(formData.initial_balance) : '')}
                        onChange={(e) => {
                          const rawValue = parseCurrency(e.target.value);
                          let value = rawValue;
                          if (formData.type === 'credit_card' && value !== '') {
                            value = value.startsWith('-') ? value : '-' + value;
                          }
                          setFormData({ ...formData, initial_balance: value });
                        }}
                        placeholder={formData.type === 'credit_card' ? 'Ej: $5,000.00' : 'Ej: $10,000.00'}
                        className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white text-[16px] sm:text-sm font-semibold"
                      />
                    </div>
                  )}

                  {/* Credit Limit (only for credit cards) */}
                  {formData.type === 'credit_card' && (
                    <div className="space-y-4">
                      {/* Límite de Crédito */}
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                        <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          Límite de Crédito *
                          <div className="relative group">
                            <HelpCircle className="w-4 h-4 text-blue-500 cursor-help" />
                            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 sm:w-72 p-3 bg-slate-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-[100] shadow-xl pointer-events-none">
                              <p className="font-semibold mb-1">💰 Límite de Crédito</p>
                              <p>Es el monto TOTAL que el banco te otorgó como crédito. Siempre es un valor positivo.</p>
                              <p className="mt-2 text-green-300">Ejemplo: Si tu límite es $50,000 → ingresa 50000</p>
                              {/* Arrow pointing down */}
                              <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-slate-900"></div>
                            </div>
                          </div>
                        </label>
                        <p className="text-xs text-slate-600 dark:text-slate-400 mb-2">
                          El monto total que te otorgó el banco
                        </p>
                        <input
                          type="text"
                          inputMode="decimal"
                          required={formData.type === 'credit_card'}
                          value={formData.credit_limit ? formatCurrencyInput(formData.credit_limit) : ''}
                          onChange={(e) => {
                            const rawValue = parseCurrency(e.target.value);
                            setFormData(prev => ({ ...prev, credit_limit: rawValue }));
                          }}
                          placeholder="Ej: $50,000.00"
                          className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white text-[16px] sm:text-sm font-semibold"
                        />
                      </div>

                      {/* Saldo Disponible - CALCULADO (Preview) */}
                      {!editingAccount && formData.credit_limit && formData.initial_balance && (
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-4 rounded-lg border-2 border-green-300 dark:border-green-700">
                          <div className="flex items-start gap-2 mb-2">
                            <Info className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                            <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                              Saldo Disponible (Calculado)
                              <div className="relative group">
                                <HelpCircle className="w-4 h-4 text-green-600 cursor-help flex-shrink-0" />
                                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 sm:w-72 p-3 bg-slate-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-[100] shadow-xl pointer-events-none">
                                  <p className="font-semibold mb-1">✅ Saldo Disponible</p>
                                  <p>Es lo que PUEDES USAR de tu tarjeta ahora. Se calcula:</p>
                                  <p className="mt-2 text-cyan-300">Disponible = Límite - Deuda</p>
                                  {/* Arrow pointing down */}
                                  <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-slate-900"></div>
                                </div>
                              </div>
                            </label>
                          </div>
                          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                            ${(parseFloat(formData.credit_limit || 0) - Math.abs(parseFloat(formData.initial_balance || 0))).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <div className="text-xs text-slate-600 dark:text-slate-400 mt-2 font-mono break-words">
                            = ${parseFloat(formData.credit_limit || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })} (límite) - ${Math.abs(parseFloat(formData.initial_balance || 0)).toLocaleString('es-MX', { minimumFractionDigits: 2 })} (deuda)
                          </div>
                          <p className="text-xs text-green-700 dark:text-green-300 mt-2">
                            💡 Crédito disponible para usar en este momento
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Credit Card Extra: Corte, Vencimiento y Recordatorio */}
                  {formData.type === 'credit_card' && (
                    <div
                      id="account-reminder-section"
                      className={`p-4 bg-gradient-to-r from-rose-50/70 to-pink-50/70 dark:bg-slate-900/40 rounded-2xl border space-y-4 transition-all duration-500 ${
                        highlightReminder
                          ? 'border-rose-500 ring-4 ring-rose-400/40 shadow-xl shadow-rose-500/10 scale-[1.01]'
                          : 'border-rose-200 dark:border-slate-700'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4 text-rose-500" />
                          <span className="text-xs font-bold text-slate-800 dark:text-slate-200">Tipo y Fechas de la Tarjeta</span>
                        </div>
                        {highlightReminder && (
                          <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full bg-rose-600 text-white animate-pulse">
                            Configuración de Recordatorio
                          </span>
                        )}
                      </div>

                      {/* Subtipo de Tarjeta: Bancaria vs Departamental */}
                      <div>
                        <label className="text-[11px] font-bold text-slate-600 dark:text-slate-400 block mb-1">
                          Clasificación de la Tarjeta
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, credit_card_type: 'bank' })}
                            className={`py-2 px-2 rounded-xl text-xs font-bold border transition-all ${
                              (formData.credit_card_type || 'bank') === 'bank'
                                ? 'bg-rose-600 text-white border-rose-600 shadow-sm'
                                : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300'
                            }`}
                          >
                            🏛️ Bancaria (BBVA, Nu, etc.)
                          </button>
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, credit_card_type: 'department' })}
                            className={`py-2 px-2 rounded-xl text-xs font-bold border transition-all ${
                              formData.credit_card_type === 'department'
                                ? 'bg-rose-600 text-white border-rose-600 shadow-sm'
                                : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300'
                            }`}
                          >
                            🏬 Departamental (Liverpool, etc.)
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        {/* Día de Corte */}
                        <div>
                          <label className="text-[11px] font-bold text-slate-600 dark:text-slate-400 block mb-1">
                            Día de Corte
                          </label>
                          <select
                            value={formData.cut_day}
                            onChange={(e) => setFormData({ ...formData, cut_day: e.target.value })}
                            className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-rose-200 dark:border-slate-600 rounded-xl text-sm font-semibold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-rose-500"
                          >
                            <option value="">-- Día --</option>
                            {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                              <option key={d} value={d}>Día {d}</option>
                            ))}
                          </select>
                          <p className="text-[10px] text-slate-500 mt-1">Cuando cierra tu ciclo</p>
                        </div>

                        {/* Día Límite de Pago */}
                        <div>
                          <label className="text-[11px] font-bold text-slate-600 dark:text-slate-400 block mb-1">
                            Límite de Pago
                          </label>
                          <select
                            value={formData.payment_due_day}
                            onChange={(e) => setFormData({ ...formData, payment_due_day: e.target.value })}
                            className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-rose-200 dark:border-slate-600 rounded-xl text-sm font-semibold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-rose-500"
                          >
                            <option value="">-- Día --</option>
                            {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                              <option key={d} value={d}>Día {d}</option>
                            ))}
                          </select>
                          <p className="text-[10px] text-slate-500 mt-1">Para no pagar intereses</p>
                        </div>
                      </div>

                      {/* Desfase de Vencimiento de Pago */}
                      <div>
                        <label className="text-[11px] font-bold text-slate-600 dark:text-slate-400 block mb-1.5">
                          Mes de Vencimiento de Pago
                        </label>
                        <div className="grid grid-cols-3 gap-1.5">
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, payment_due_offset_months: 0 })}
                            className={`py-2 px-2 rounded-xl text-xs font-bold border transition-all text-center ${
                              (formData.payment_due_offset_months ?? 0) === 0
                                ? 'bg-rose-600 text-white border-rose-600 shadow-sm'
                                : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300'
                            }`}
                          >
                            Mismo mes
                            <span className="block text-[9px] font-normal opacity-80">(Predeterminado)</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, payment_due_offset_months: 1 })}
                            className={`py-2 px-2 rounded-xl text-xs font-bold border transition-all text-center ${
                              Number(formData.payment_due_offset_months) === 1
                                ? 'bg-rose-600 text-white border-rose-600 shadow-sm'
                                : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300'
                            }`}
                          >
                            +1 mes
                            <span className="block text-[9px] font-normal opacity-80">(Mes siguiente)</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, payment_due_offset_months: 2 })}
                            className={`py-2 px-2 rounded-xl text-xs font-bold border transition-all text-center ${
                              Number(formData.payment_due_offset_months) === 2
                                ? 'bg-rose-600 text-white border-rose-600 shadow-sm'
                                : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300'
                            }`}
                          >
                            +2 meses
                            <span className="block text-[9px] font-normal opacity-80">(2 meses después)</span>
                          </button>
                        </div>
                        <p className="text-[10px] text-slate-500 mt-1">
                          {(formData.payment_due_offset_months ?? 0) === 0 && 'La fecha límite vence en el mismo mes calendario que el corte.'}
                          {Number(formData.payment_due_offset_months) === 1 && 'La fecha límite vence 1 mes después del corte (ej. corte oct, pago nov).'}
                          {Number(formData.payment_due_offset_months) === 2 && 'La fecha límite vence 2 meses después del corte.'}
                        </p>
                      </div>

                      {/* Recordatorio automático */}
                      <div className="flex items-start gap-3 pt-1">
                        <input
                          type="checkbox"
                          id="auto_reminder"
                          checked={formData.auto_reminder}
                          onChange={(e) => setFormData({ ...formData, auto_reminder: e.target.checked })}
                          className="w-4 h-4 mt-0.5 text-rose-600 rounded focus:ring-rose-500 cursor-pointer"
                        />
                        <div>
                          <label htmlFor="auto_reminder" className="text-xs font-bold text-slate-800 dark:text-slate-200 cursor-pointer">
                            Crear recordatorio de pago automático
                          </label>
                          <p className="text-[10px] text-slate-500 mt-0.5">
                            Se creará un recordatorio mensual {formData.payment_due_day ? `para el día ${formData.payment_due_day}` : 'al día límite configurado'} con aviso 3 días antes
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Yield Section — solo para Billetera Digital o Inversión */}
                  {['digital_wallet', 'investment'].includes(formData.type) && (
                    <div className="p-4 bg-gradient-to-r from-blue-50/70 to-indigo-50/70 dark:bg-slate-900/40 rounded-2xl border border-blue-200 dark:border-slate-700 space-y-3 overflow-hidden w-full max-w-full">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Zap className="w-4 h-4 text-amber-500 shrink-0" />
                          <label htmlFor="has_yield" className="text-xs font-bold text-slate-800 dark:text-slate-200 cursor-pointer">
                            ¿Genera Rendimiento Anual?
                          </label>
                        </div>
                        <input
                          type="checkbox"
                          id="has_yield"
                          checked={formData.has_yield}
                          onChange={(e) => setFormData({ ...formData, has_yield: e.target.checked })}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer shrink-0"
                        />
                      </div>

                      {formData.has_yield && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 w-full max-w-full">
                          <div className="min-w-0 w-full">
                            <label className="text-[11px] font-bold text-slate-600 dark:text-slate-400 block mb-1">
                              Tasa Anual (%) *
                            </label>
                            <div className="relative w-full">
                              <input
                                type="text"
                                inputMode="decimal"
                                value={formData.annual_yield_rate}
                                onChange={(e) => {
                                  let v = e.target.value.replace(/[^\d.]/g, '');
                                  setFormData({ ...formData, annual_yield_rate: v });
                                }}
                                placeholder="Ej: 13.5"
                                className="w-full min-w-0 max-w-full block box-border px-3 py-2 bg-white dark:bg-slate-800 border border-blue-200 dark:border-slate-600 rounded-xl text-sm font-bold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 pr-7"
                                required={formData.has_yield}
                              />
                              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">%</span>
                            </div>
                          </div>

                          <div className="min-w-0 w-full">
                            <label className="text-[11px] font-bold text-slate-600 dark:text-slate-400 block mb-1">
                              Fecha de inicio *
                            </label>
                            <div className="relative w-full max-w-full overflow-hidden rounded-xl">
                              <input
                                type="date"
                                value={formData.yield_start_date}
                                onChange={(e) => setFormData({ ...formData, yield_start_date: e.target.value })}
                                className="w-full block box-border min-w-0 max-w-full px-3 py-2 bg-white dark:bg-slate-800 border border-blue-200 dark:border-slate-600 rounded-xl text-sm font-semibold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                style={{ colorScheme: 'light', maxWidth: '100%', boxSizing: 'border-box' }}
                                required={formData.has_yield}
                              />
                            </div>
                          </div>

                          <p className="text-[10px] text-blue-700 dark:text-blue-300 sm:col-span-2 leading-relaxed">
                            ℹ️ Se calculará el rendimiento diario acumulado sobre el capital a partir de esta fecha.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Datos para recibir transferencias (SPEI) */}
                  <div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-3">
                    <div className="flex items-center gap-2">
                      <Building className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                      <span className="text-xs font-bold text-slate-800 dark:text-slate-200">
                        Datos para recibir transferencias (SPEI)
                      </span>
                      <span className="text-[10px] bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-1.5 py-0.5 rounded font-medium ml-auto">
                        Opcional
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                      Si configuras estos datos, se precargarán automáticamente al desglosar compras y generar ligas de cobro.
                    </p>
                    <div className="space-y-2.5">
                      <div>
                        <label className="text-[11px] font-bold text-slate-600 dark:text-slate-400 block mb-1">
                          Titular de la cuenta
                        </label>
                        <input
                          type="text"
                          placeholder="Nombre completo del titular"
                          value={formData.holder_name || ''}
                          onChange={(e) => setFormData({ ...formData, holder_name: e.target.value })}
                          className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl text-sm font-semibold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        <div>
                          <label className="text-[11px] font-bold text-slate-600 dark:text-slate-400 block mb-1">
                            Banco / Institución
                          </label>
                          <input
                            type="text"
                            placeholder="Ej: Nu, BBVA, Santander"
                            value={formData.bank_name || ''}
                            onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                            className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl text-sm font-semibold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                        <div>
                          <label className="text-[11px] font-bold text-slate-600 dark:text-slate-400 block mb-1">
                            CLABE (18 dígitos)
                          </label>
                          <input
                            type="text"
                            maxLength={18}
                            placeholder="012345678901234567"
                            value={formData.clabe || ''}
                            onChange={(e) => setFormData({ ...formData, clabe: e.target.value.replace(/\D/g, '') })}
                            className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl text-sm font-mono font-bold text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Color */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Color ({COLORS.find(c => c.value === formData.color)?.label || 'Personalizado'})
                    </label>
                    <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                      {COLORS.map(color => (
                        <button
                          key={color.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, color: color.value })}
                          className={`w-full h-10 rounded-xl transition-all flex items-center justify-center ${
                            formData.color === color.value ? 'ring-2 ring-offset-2 ring-blue-500 scale-105 shadow-md' : 'hover:scale-105'
                          }`}
                          style={{ backgroundColor: color.value }}
                          title={color.label}
                        >
                          {formData.color === color.value && (
                            <Check className="w-4 h-4 text-white drop-shadow" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Default Account */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_default"
                      checked={formData.is_default}
                      onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="is_default" className="text-sm text-slate-700 dark:text-slate-300">
                      Establecer como cuenta predeterminada
                    </label>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={handleCloseDialog}
                      className="flex-1 px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      {editingAccount ? 'Guardar Cambios' : 'Crear Cuenta'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && selectedAccount && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md p-6">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">
                ¿Eliminar cuenta?
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                ¿Estás seguro de que deseas eliminar <strong>{selectedAccount.name}</strong>?
                Esta acción no se puede deshacer y <strong>solo funciona si no tiene transacciones</strong>.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setSelectedAccount(null);
                  }}
                  className="flex-1 px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-white rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleDeleteAccount(selectedAccount.id)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Transacción (abre por default en transferencia con cuenta origen) */}
        <TransactionModal
          isOpen={isTransactionModalOpen}
          onClose={() => {
            setIsTransactionModalOpen(false);
            setTransactionSourceAccountId(null);
          }}
          preselectedType="transfer"
          preselectedAccountId={transactionSourceAccountId}
          onSuccess={() => {
            fetchAccounts();
            window.dispatchEvent(new CustomEvent('transaction-added'));
          }}
        />

        {/* Modal Exclusivo de Desglose para Tarjeta de Crédito */}
        <CreditCardBreakdownModal
          isOpen={isBreakdownModalOpen}
          onClose={() => {
            setIsBreakdownModalOpen(false);
            setSelectedAccountForBreakdown(null);
          }}
          onSuccess={() => fetchAccounts()}
          account={selectedAccountForBreakdown}
          initialEntryType="breakdown"
        />

        {/* ─── MODAL BOTTOM-SHEET: FILTRO DE MONEDA ─── */}
        <MobileBottomSheet isOpen={showCurrencyModal} onClose={() => setShowCurrencyModal(false)}>
          {({ close }) => (
            <>
              <div className="flex items-center justify-between pb-1 border-b border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-xl bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                    <Filter className="w-4 h-4" />
                  </div>
                  <h3 className="text-lg font-black text-slate-900 dark:text-white">Filtrar por Moneda</h3>
                </div>
                <button
                  type="button"
                  onClick={close}
                  className="p-2 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Moneda de las cuentas</label>
                <div className="grid grid-cols-1 gap-2">
                  {[
                    { id: 'all', label: 'Todas las monedas' },
                    { id: 'MXN', label: 'Peso Mexicano (MXN)' },
                    { id: 'USD', label: 'Dólar Americano (USD)' }
                  ].map(opt => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => {
                        setFilterCurrency(opt.id);
                        close();
                      }}
                      className={`py-3 px-4 rounded-xl text-xs font-bold border transition-all flex items-center justify-between ${
                        filterCurrency === opt.id
                          ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                          : 'bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200/80 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-750'
                      }`}
                    >
                      <span>{opt.label}</span>
                      {filterCurrency === opt.id && <Check className="w-4 h-4" />}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => {
                    setFilterCurrency('all');
                    close();
                  }}
                  className="flex-1 py-3 px-4 rounded-2xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-700 dark:text-slate-300 text-xs font-bold active:scale-95 transition-all"
                >
                  Restablecer
                </button>
                <button
                  type="button"
                  onClick={close}
                  className="flex-[2] py-3 px-4 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold shadow-lg shadow-blue-200 dark:shadow-none active:scale-95 transition-all"
                >
                  Listo
                </button>
              </div>
            </>
          )}
        </MobileBottomSheet>

        {/* ─── MODAL BOTTOM-SHEET: ORGANIZAR CUENTAS ─── */}
        <MobileBottomSheet isOpen={showSortModal} onClose={() => setShowSortModal(false)}>
          {({ close }) => (
            <>
              <div className="flex items-center justify-between pb-1 border-b border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-xl bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                    <ArrowUpDown className="w-4 h-4" />
                  </div>
                  <h3 className="text-lg font-black text-slate-900 dark:text-white">Organizar Cuentas</h3>
                </div>
                <button
                  type="button"
                  onClick={close}
                  className="p-2 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Criterio de ordenación</label>
                <div className="grid grid-cols-1 gap-2">
                  {[
                    { id: 'name', label: 'Alfabético (A – Z)' },
                    { id: 'balance-high', label: 'Mayor saldo ($ Mayor a menor)' },
                    { id: 'balance-low', label: 'Menor saldo ($ Menor a mayor)' },
                    { id: 'date-new', label: 'Más reciente' },
                    { id: 'date-old', label: 'Más antigua' }
                  ].map(opt => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => {
                        setSortBy(opt.id);
                        close();
                      }}
                      className={`py-3 px-4 rounded-xl text-xs font-bold border transition-all flex items-center justify-between ${
                        sortBy === opt.id
                          ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                          : 'bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200/80 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-750'
                      }`}
                    >
                      <span>{opt.label}</span>
                      {sortBy === opt.id && <Check className="w-4 h-4" />}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => {
                    setSortBy('name');
                    close();
                  }}
                  className="flex-1 py-3 px-4 rounded-2xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-700 dark:text-slate-300 text-xs font-bold active:scale-95 transition-all"
                >
                  Restablecer
                </button>
                <button
                  type="button"
                  onClick={close}
                  className="flex-[2] py-3 px-4 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold shadow-lg shadow-blue-200 dark:shadow-none active:scale-95 transition-all"
                >
                  Listo
                </button>
              </div>
            </>
          )}
        </MobileBottomSheet>
        {/* ─── MODAL BOTTOM-SHEET: ACCIONES DE CUENTA ─── */}
        <MobileBottomSheet isOpen={!!accountForActions} onClose={() => setAccountForActions(null)}>
          {({ close }) => {
            if (!accountForActions) return null;
            const Icon = getTypeIcon(accountForActions.type);
            const isCC = accountForActions.type === 'credit_card';

            return (
              <div className="space-y-3">
                {/* Cabecera con info de la cuenta */}
                <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 shadow-xs"
                      style={{ backgroundColor: `${accountForActions.color}18` }}
                    >
                      <Icon className="w-6 h-6" style={{ color: accountForActions.color }} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-bold text-base text-slate-900 dark:text-white truncate">
                        {accountForActions.name}
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {ACCOUNT_TYPES.find(t => t.value === accountForActions.type)?.label || accountForActions.type} · <span className="font-semibold text-slate-700 dark:text-slate-300">{accountForActions.currency}</span>
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={close}
                    className="p-2 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 shrink-0"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Saldo destacado */}
                <div className="p-3 bg-slate-50 dark:bg-slate-800/60 rounded-2xl flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                    {isCC ? 'Deuda actual' : 'Saldo disponible'}
                  </span>
                  <span className={`text-base font-black ${isCC ? 'text-rose-600 dark:text-rose-400' : 'text-slate-900 dark:text-white'}`}>
                    {hideBalances ? '••••••' : isCC
                      ? `-${formatCurrency(Math.abs(accountForActions.current_balance || 0), accountForActions.currency)}`
                      : formatCurrency(accountForActions.current_balance, accountForActions.currency)}
                  </span>
                </div>

                {/* Lista de Acciones */}
                <div className="space-y-1.5 pt-1">
                  {/* Editar */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.currentTarget?.blur();
                      const acc = accountForActions;
                      close();
                      setTimeout(() => handleOpenDialog(acc), 240);
                    }}
                    className="w-full p-3 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-200/70 dark:border-slate-700/60 flex items-center gap-3 text-left transition-all active:scale-[0.99]"
                  >
                    <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
                      <Edit2 className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900 dark:text-white">Editar Cuenta</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">Nombre, límites, rendimiento y estilo</p>
                    </div>
                  </button>

                  {/* Generar Transacción (por default transferencia entre cuentas) */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.currentTarget?.blur();
                      const accId = accountForActions.id;
                      close();
                      setTimeout(() => {
                        if (document.activeElement && typeof document.activeElement.blur === 'function') {
                          document.activeElement.blur();
                        }
                        setTransactionSourceAccountId(accId);
                        setIsTransactionModalOpen(true);
                      }, 240);
                    }}
                    className="w-full p-3 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-200/70 dark:border-slate-700/60 flex items-center gap-3 text-left transition-all active:scale-[0.99]"
                  >
                    <div className="w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0">
                      <ArrowRightLeft className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900 dark:text-white">Generar Transacción</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">Movimiento o transferencia entre cuentas</p>
                    </div>
                  </button>

                  {/* Desglosar Saldo (exclusivo para Tarjetas de Crédito) */}
                  {isCC && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.currentTarget?.blur();
                        const acc = accountForActions;
                        close();
                        setTimeout(() => {
                          if (document.activeElement && typeof document.activeElement.blur === 'function') {
                            document.activeElement.blur();
                          }
                          setSelectedAccountForBreakdown(acc);
                          setIsBreakdownModalOpen(true);
                        }, 240);
                      }}
                      className="w-full p-3 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-200/70 dark:border-slate-700/60 flex items-center gap-3 text-left transition-all active:scale-[0.99]"
                    >
                      <div className="w-9 h-9 rounded-xl bg-purple-50 dark:bg-purple-950/50 text-purple-600 dark:text-purple-400 flex items-center justify-center shrink-0">
                        <CreditCard className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-slate-900 dark:text-white">Desglosar Saldo</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">Compras a meses, retenciones y préstamos</p>
                      </div>
                    </button>
                  )}

                  {/* Ver Movimientos */}
                  <button
                    type="button"
                    onClick={() => {
                      const accId = accountForActions.id;
                      close();
                      setTimeout(() => {
                        sessionStorage.setItem('return_to_account_id', accId);
                        navigate(`/accounts/${accId}`);
                      }, 240);
                    }}
                    className="w-full p-3 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-200/70 dark:border-slate-700/60 flex items-center gap-3 text-left transition-all active:scale-[0.99]"
                  >
                    <div className="w-9 h-9 rounded-xl bg-purple-50 dark:bg-purple-950/50 text-purple-600 dark:text-purple-400 flex items-center justify-center shrink-0">
                      <ArrowUpDown className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900 dark:text-white">Ver Movimientos</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">Historial detallado de transacciones</p>
                    </div>
                  </button>

                  {/* Archivar */}
                  <button
                    type="button"
                    onClick={() => {
                      const accId = accountForActions.id;
                      close();
                      setTimeout(() => handleArchiveAccount(accId), 240);
                    }}
                    className="w-full p-3 rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-200/70 dark:border-slate-700/60 flex items-center gap-3 text-left transition-all active:scale-[0.99]"
                  >
                    <div className="w-9 h-9 rounded-xl bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0">
                      <Archive className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900 dark:text-white">Archivar Cuenta</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">Ocultar de la lista sin perder registros</p>
                    </div>
                  </button>

                  {/* Eliminar */}
                  <button
                    type="button"
                    onClick={() => {
                      const acc = accountForActions;
                      close();
                      setTimeout(() => {
                        setSelectedAccount(acc);
                        setShowDeleteConfirm(true);
                      }, 240);
                    }}
                    className="w-full p-3 rounded-2xl hover:bg-red-50 dark:hover:bg-red-950/30 border border-red-200/70 dark:border-red-900/40 flex items-center gap-3 text-left transition-all active:scale-[0.99]"
                  >
                    <div className="w-9 h-9 rounded-xl bg-red-100 dark:bg-red-950/60 text-red-600 dark:text-red-400 flex items-center justify-center shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-red-600 dark:text-red-400">Eliminar Cuenta</p>
                      <p className="text-xs text-red-500/80 dark:text-red-400/80 truncate">Solo si no tiene transacciones registradas</p>
                    </div>
                  </button>
                </div>

                <div className="pt-2">
                  <button
                    type="button"
                    onClick={close}
                    className="w-full py-3 rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-bold text-xs active:scale-95 transition-all"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            );
          }}
        </MobileBottomSheet>
      </div>
    </Layout>
  );
};

export default Accounts;
